/**
 * @file Firebase init — singleton app/auth/db/functions exports for the rest
 * of the codebase. Reads VITE_* env vars at build time.
 *
 * If VITE_USE_EMULATORS=true, the dev server connects to local emulators
 * (firestore: 8080, auth: 9099, functions: 5001).
 */

import { initializeApp }                                from 'firebase/app';
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator }       from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator }       from 'firebase/functions';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.warn(
    '[gear] Firebase env vars missing — copy .env.example to .env and fill in ' +
    'VITE_FIREBASE_* values from the Firebase console.',
  );
}

export const app  = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db   = getFirestore(app);
// Functions live in europe-west3 — match brand.strv.com's eur3 multi-region.
export const fns  = getFunctions(app, 'europe-west3');

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt:   'select_account',     // always show account picker (per spec)
  hd:       'strv.com',           // Workspace hint — non-strv accounts still appear
});

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  console.info('[gear] connecting to local Firebase emulators');
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(fns, '127.0.0.1', 5001);
}
